import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import infoicon from "../../../../Images/infoicon.svg";
import { hasAdminAccess } from "../../../../Utils/permissionUtils";
import { UserRoles } from "../../../../Constants/userRoles";
import helpers from "../../../../Utils/helpers";
import SkynetIcon from "../../../Fundamentals/SkynetIcon";
import Tooltip, { TooltipContent, TooltipTrigger } from "../../../Overlays/Tooltip";
import { VossProductGroupType } from "../../../../Constants/VOSS/vossProductGroupType";
import { VossProductType } from "../../../../Constants/VOSS/vossProductType";
import { VossProductClassification } from "../../../../Constants/VOSS/vossProductClassification";
import customerRoutes from "../../../../Constants/Customer/customerRoutes";
import { VossSubscriptionBillingPeriodKind } from '../../../../Constants/VOSS/vossSubscriptionBillingPeriodKind';
import targetGroupHelpers from '../../../../Utils/targetGroups';
import ExpandableTable from "../../../Fundamentals/Tables/ExpandedableTable";
import subscriptionUtils from "../../../../Utils/subscriptionUtils";

const Subscriptions = ({ subscriptions, customer, vonAccounts, user, detailedSubscriptions, handleRepublishEvent }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [licensesDictionary, setLicensesDictionary] = useState();
    const customerIsNonInteractive = targetGroupHelpers.isNonInteractive(customer);

    useEffect(() => {
        const newLicensesDictionary = vonAccounts?.reduce((acc, account) => {
            account.roles.forEach(role => {
                if (role.licenseId !== null) {
                    if (!acc[role.subscriptionId]) {
                        acc[role.subscriptionId] = [];
                    }
                    acc[role.subscriptionId].push({
                        ...account,
                        licenseRole: role
                    });
                }
            });
            return acc;
        }, {});
        setLicensesDictionary(newLicensesDictionary);
    }, [vonAccounts]);

    const getBillingStartDate = (subscription) => {
        switch (subscription.subscriptionBillingPeriod.periodKind) {
            case VossSubscriptionBillingPeriodKind.NotBillable:
                return subscription.subscriptionBillingPeriod.endDate
            default:
                return subscription.subscriptionBillingPeriod.startDate
        }
    }

    const renderPendingChangesIcon = (subscription) => {
        const detailed = detailedSubscriptions?.find(d => d.id === subscription.id);
        const pendingChanges = subscriptionUtils.getPendingScheduledChanges(detailed);
        if (pendingChanges.length === 0) return null;

        return (
            <Tooltip placement="top">
                <TooltipTrigger>
                    <SkynetIcon icon="vismaicon-sm vismaicon-time" />
                </TooltipTrigger>
                <TooltipContent>
                    {pendingChanges.map((change, i) => (
                        <div key={i}>
                            {t('scheduledsubscriptionchanges.scheduledChange', {
                                change: [change.tier?.name, change.plan?.name].filter(Boolean).join(' & '),
                                date: helpers.formatDate(change.scheduledDate)
                            })}
                        </div>
                    ))}
                </TooltipContent>
            </Tooltip>
        );
    }

    const renderEditColumn = (subscription) => {
        const blockEdit = subscriptionUtils.tierIsNonInteractive(subscription.tier) || subscriptionUtils.isInCollaboration(subscription);

        return ((helpers.isAoCustomer(customer.vbCustomer) && !hasAdminAccess(user, UserRoles.AOSubscriptionManager))
            ? <span>-</span>
            : <div className="d-flex justify-content-center">
                {blockEdit ?
                    <Tooltip placement="left" arrowPlacement="left in">
                        <TooltipTrigger>
                            {<>
                                <SkynetIcon icon={'vismaicon-edit disabled'} />
                            </>}
                        </TooltipTrigger>
                        <TooltipContent>
                            {t('subscriptions.nonInteractiveEditNotAllowed')}
                        </TooltipContent>
                    </Tooltip>
                    :
                    <SkynetIcon icon={'vismaicon-edit'} onClick={() => navigate(customerRoutes.customer(customer.pathId, customerRoutes.editSubscription(subscription.id)))} />
                }
            </div>
        );
    }

    const renderLicenses = (licenses) => {
        return <table className="table table-condensed">
            <thead>
                <tr>
                    <th>{t('subscriptions.name')}</th>
                    <th>{t('subscriptions.email')}</th>
                    <th>{t('subscriptions.role')}</th>
                </tr>
            </thead>
            <tbody>
                {licenses?.map((account, index) => {
                    return <tr key={index}>
                        <td>{account.name}</td>
                        <td>{account.email}</td>
                        <td>{account.licenseRole.role}</td>
                    </tr>
                })}</tbody>
        </table>
    }

    const onHandleRepublishEvent = (product) => {
        handleRepublishEvent(product);
    }

    return (
        <ExpandableTable>
            <thead>
                <tr key="thead">
                    <th className="text-left">{t('subscriptions.package')}</th>
                    <th className="text-left">{t('subscriptions.tier')}</th>
                    <th className="text-left">{t('subscriptions.licensesused')}</th>
                    <th className="text-left">{t('subscriptions.collaboration')}</th>
                    <th className="text-left">{t('subscriptions.purchasedate')}</th>
                    <th className="text-left">{t('subscriptions.billingStartDate')}</th>
                    <th className="text-left">{t('subscriptions.periodEndDate')}</th>
                    <th className="text-left">{t('subscriptions.period')}</th>
                    <th className="text-left">{t('subscriptions.terminationDate')}</th>
                    <th className="text-center">
                        <div className="d-flex">
                            {t('subscriptions.edit')}
                            {(helpers.isAoCustomer(customer.vbCustomer) && !hasAdminAccess(user, UserRoles.AOSubscriptionManager)) &&
                                <Tooltip>
                                    <TooltipTrigger>
                                        {<>
                                            <img className="ml-8 mb-4" src={infoicon} alt="Info" />
                                        </>}
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {t('subscriptions.aocustomer')}
                                    </TooltipContent>
                                </Tooltip>

                            }
                        </div>
                    </th>
                </tr>
            </thead>
            <ExpandableTable.Body>
                {subscriptions?.map(subscription => (
                    <ExpandableTable.Row key={subscription.id}>
                        <ExpandableTable.Row.Expandable>
                            <td>
                                <span className="d-inline-flex align-items-center gap-8">
                                    {subscription.package.name}
                                    {renderPendingChangesIcon(subscription)}
                                </span>
                            </td>
                            <td>{subscription.tier.name}</td>
                            <td>{licensesDictionary && licensesDictionary[subscription.id]?.length}</td>
                            <td className="text-center">
                                {subscriptionUtils.isInCollaboration(subscription) ? (
                                    <SkynetIcon 
                                        icon="vismaicon-filled vismaicon-success" 
                                        title={t('subscriptions.collaborationTooltip')} 
                                        onClick={() => navigate(customerRoutes.Collaborations)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                navigate(customerRoutes.Collaborations);
                                            }
                                        }}
                                    />
                                ) : (
                                    <span>-</span>
                                )}
                            </td>
                            <td>{helpers.formatDate(subscription.created)}</td>
                            <td>{helpers.formatDate(getBillingStartDate(subscription))}</td>
                            <td>{helpers.formatDate(subscription.periodEnd)}</td>
                            <td>{subscription.plan.name}</td>
                            <td>{subscription.termination && helpers.formatDate(subscription.termination?.scheduledDate)}</td>
                            <td>{renderEditColumn(subscription)}</td>
                        </ExpandableTable.Row.Expandable>
                        <ExpandableTable.Row.Expanded>
                            <ExpandableTable.Row>
                                <ExpandableTable.Row.Expandable colSpan={100}>
                                    <td>{t("subscriptions.includedProducts")}</td>
                                </ExpandableTable.Row.Expandable>
                                <ExpandableTable.Row.Expanded>
                                    {subscription.productGroups
                                        ?.filter(group => group.productGroupType === VossProductGroupType.Included)
                                        ?.map(group =>
                                            (group.products ?? [])
                                                .filter(p => p.type !== VossProductType.Usage && p.classification !== VossProductClassification.RevenueCarrier)
                                                .map(product => (
                                                    <tr key={`${group.id}-${product.id}`}>
                                                        <td colSpan={9}>{product.name}</td>
                                                        <td className='text-center'>
                                                            {product.type === VossProductType.Main && (
                                                                <button type="button" className="btn btn-link p-0" onClick={() => onHandleRepublishEvent(product)}>
                                                                    {t('subscriptions.republishEvent')}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                        )
                                    }
                                </ExpandableTable.Row.Expanded>
                            </ExpandableTable.Row>
                            <ExpandableTable.Row>
                                <ExpandableTable.Row.Expandable colSpan={100}>
                                    <td colSpan={100}>{t('subscriptions.optionalProducts')}</td>
                                </ExpandableTable.Row.Expandable>
                                <ExpandableTable.Row.Expanded>
                                    {subscription.productGroups
                                        ?.filter((group) => group.productGroupType === VossProductGroupType.Optional)
                                        ?.map((group) =>
                                            (group.products ?? [])
                                                .filter((p) => (p.type === VossProductType.Main || p.type === VossProductType.Addon) && p.classification !== VossProductClassification.RevenueCarrier)
                                                .map((product) => (
                                                    <tr key={`${group.id}-${product.id}`}>
                                                        <td colSpan={9}>
                                                            {product.name}
                                                        </td>
                                                        <td className="text-center">
                                                            {product.type === VossProductType.Main && (
                                                                <button type="button" className="btn btn-link p-0" onClick={() => onHandleRepublishEvent(product)}>
                                                                    {t('subscriptions.republishEvent')}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                        )}
                                </ExpandableTable.Row.Expanded>
                            </ExpandableTable.Row>
                            <ExpandableTable.Row>
                                <ExpandableTable.Row.Expandable colSpan={100}>
                                    <td>{t('subscriptions.licenses')}</td>
                                </ExpandableTable.Row.Expandable>
                                <ExpandableTable.Row.Expanded>
                                    <tr>
                                        <td colSpan={100}>
                                            {renderLicenses(licensesDictionary && licensesDictionary[subscription.id])}
                                        </td>
                                    </tr>
                                </ExpandableTable.Row.Expanded>
                            </ExpandableTable.Row>
                        </ExpandableTable.Row.Expanded>
                    </ExpandableTable.Row>
                ))}
            </ExpandableTable.Body>
        </ExpandableTable>
    );
}
export default Subscriptions;